/**
 * Cash & stock intelligence — where owner money is stuck and what to do.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
  formatCompactInrAxis as formatCompact,
} from "../../../utils/reportFormat";
import {
  getFuelStatusRowClass,
  getMarginHeatVisual,
  getStockIdleAgeVisual,
} from "../../../utils/workingCapitalAgeColors";

const formatLakhs = (value) => formatCompact(value);

const statusClass = (status) => {
  const map = {
    critical: "owner-wc-status--critical",
    warning: "owner-wc-status--warning",
    ok: "owner-wc-status--ok",
  };
  return map[status] || "";
};

function ChartWrap({ height = 260, children }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      {children}
    </ResponsiveContainer>
  );
}

function StockAgeIndicator({ daysSinceSale }) {
  const visual = getStockIdleAgeVisual(daysSinceSale);

  return (
    <div className="owner-wc-age-indicator">
      <span className={`owner-wc-status ${visual.badgeClass}`} title={visual.label}>
        {daysSinceSale == null ? "Never" : formatNumber(daysSinceSale, 0)}
      </span>
      <span className="owner-wc-age-bar" aria-hidden="true">
        <span
          className="owner-wc-age-bar__fill"
          style={{ width: `${visual.barPercent}%`, backgroundColor: visual.color }}
        />
      </span>
    </div>
  );
}

function LockedBreakdownTooltip({ active, payload }) {
  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0]?.payload;
  if (!row) {
    return null;
  }
  return (
    <div className="owner-wc-tooltip">
      <strong>{row.label}</strong>
      <div>{formatCurrency(row.value)}</div>
    </div>
  );
}

function FuelCoverTooltip({ active, payload, targetDays }) {
  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0]?.payload;
  if (!row) {
    return null;
  }
  return (
    <div className="owner-wc-tooltip">
      <strong>{row.fuel}</strong>
      {row.no_recent_sales ? (
        <div>No fuel sales in selected period</div>
      ) : (
        <div>{formatNumber(row.days_cover, 0)} days of stock (target {targetDays})</div>
      )}
      <div>Sold: {formatNumber(row.sold_litres_period, 0)} L</div>
      <div>Cash locked: {formatCurrency(row.locked_value)}</div>
    </div>
  );
}

export default function OwnerWorkingCapitalPanel({ workingCapital = {} }) {
  const summary = workingCapital.summary || {};
  const assumptions = workingCapital.assumptions || {};
  const lockedChart = workingCapital.locked_breakdown_chart || [];
  const fuelChart = workingCapital.fuel_stock_chart || [];
  const fuelGrades = workingCapital.fuel_grades || [];
  const fuelOpportunityInsights = workingCapital.fuel_opportunity_insights || [];
  const slowNf = workingCapital.slow_non_fuel || [];
  const lowMargin = workingCapital.low_margin_high_volume || [];

  if (!summary.total_locked && fuelGrades.length === 0 && lockedChart.length === 0) {
    return null;
  }

  const targetDays = assumptions.target_fuel_cover_days || 14;
  const chartData = fuelChart.map((row) => ({
    ...row,
    chart_days: row.no_recent_sales ? null : row.days_cover,
  }));
  const hasFuelChart = chartData.some((row) => row.chart_days != null || row.no_recent_sales);

  return (
    <div className="col-md-12 mb-3 owner-working-capital">
      <div className="dash-card owner-chart-card">
        <h5 className="mb-1">Cash &amp; stock intelligence</h5>
        <p className="owner-chart-panel__subtitle mb-2">
          {workingCapital.owner_message ||
            "Where your money is tied up and whether profit beats keeping cash in a Kerala bank FD."}
        </p>
        {assumptions.formula_note ? (
          <p className="small text-muted mb-3">
            <strong>How we calculate:</strong> {assumptions.formula_note}
            {assumptions.od_interest_pct ? (
              <span>
                {" "}
                ({assumptions.interest_rate_label || "Kerala bank FD (1 year)"}:{" "}
                {formatNumber(assumptions.od_interest_pct, 2)}% p.a.)
              </span>
            ) : null}
          </p>
        ) : null}

        {!summary.has_period_fuel_sales && fuelGrades.length > 0 ? (
          <div className="alert alert-warning py-2 px-3 small mb-3 owner-wc-alert">
            <strong>No fuel sales in this date range.</strong> Tank stock is still shown, but days-of-cover needs
            recent sales — try the pump&apos;s last operating period or a shorter recent window.
          </div>
        ) : null}

        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div className="owner-wc-kpi">
              <span className="owner-wc-kpi__label">Total cash tied up</span>
              <span className="owner-wc-kpi__value">{formatCurrency(summary.total_locked)}</span>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="owner-wc-kpi">
              <span className="owner-wc-kpi__label">Est. monthly FD opportunity cost</span>
              <span className="owner-wc-kpi__value text-danger">
                {formatCurrency(summary.monthly_interest_cost)}
              </span>
              <span className="owner-wc-kpi__hint">
                If this cash were in a 1-year Kerala bank FD instead of stock/credit
              </span>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="owner-wc-kpi">
              <span className="owner-wc-kpi__label">Stock in tanks &amp; shop</span>
              <span className="owner-wc-kpi__value">{formatCurrency(summary.inventory_locked)}</span>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="owner-wc-kpi">
              <span className="owner-wc-kpi__label">Customer credit due</span>
              <span className="owner-wc-kpi__value">{formatCurrency(summary.receivables_locked)}</span>
            </div>
          </div>
        </div>

        <div className="row g-3 mb-4">
          {lockedChart.length > 0 ? (
            <div className="col-lg-6">
              <h6 className="mb-2">Where is my cash stuck?</h6>
              <p className="small text-muted mb-2">How your working money is split across stock and credit.</p>
              <ChartWrap height={240}>
                <BarChart data={lockedChart} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tickFormatter={formatLakhs} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11 }} />
                  <Tooltip content={<LockedBreakdownTooltip />} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
                    {lockedChart.map((entry) => (
                      <Cell key={entry.key} fill={entry.color || "#5D38DB"} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartWrap>
            </div>
          ) : null}

          {hasFuelChart ? (
            <div className="col-lg-6">
              <h6 className="mb-2">Fuel stock — days of cover</h6>
              <p className="small text-muted mb-2">
                How long current tank stock lasts at recent sales speed. Green = healthy, amber = high stock,
                red = cash trapped. Target line: {targetDays} days.
              </p>
              <ChartWrap height={240}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fuel" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} label={{ value: "Days", angle: -90, position: "insideLeft", fontSize: 10 }} />
                  <Tooltip content={<FuelCoverTooltip targetDays={targetDays} />} />
                  <ReferenceLine y={targetDays} stroke="#E85D75" strokeDasharray="4 4" label={{ value: "Target", fontSize: 10 }} />
                  <Bar dataKey="chart_days" name="days_cover" radius={[4, 4, 0, 0]} maxBarSize={40}>
                    {chartData.map((entry) => (
                      <Cell
                        key={entry.fuel}
                        fill={
                          entry.no_recent_sales
                            ? "#CBD5E1"
                            : entry.status === "critical"
                              ? "#E85D75"
                              : entry.status === "warning"
                                ? "#F5A623"
                                : "#36B37E"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ChartWrap>
              {chartData.some((row) => row.no_recent_sales) ? (
                <p className="small text-muted mb-0 mt-1">
                  Grey bars = no sales in period (see table below for stock value).
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        {fuelOpportunityInsights.length > 0 ? (
          <>
            <h6 className="mb-2">Opportunity cost — slow vs fast grades</h6>
            <p className="small text-muted mb-2">
              Compares trapped stock days with how often faster grades (e.g. Petrol, Diesel) clear tanks and earn
              margin — the profit you miss by keeping cash in a slow mover.
            </p>
            <div className="owner-wc-opportunity-list mb-4">
              {fuelOpportunityInsights.map((insight) => (
                <div
                  key={insight.slow_fuel}
                  className={`owner-wc-opportunity-card owner-wc-opportunity-card--${insight.status || "warning"}`}
                >
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                    <strong>{insight.slow_fuel}</strong>
                    <span className="owner-wc-opportunity-missed text-danger">
                      ~{formatCurrency(insight.best_missed_profit)} missed vs {insight.best_benchmark}
                    </span>
                  </div>
                  <p className="small mb-2 mb-md-1">{insight.headline}</p>
                  <p className="small text-muted mb-2">{insight.owner_tip}</p>
                  {(insight.comparisons || []).map((cmp) => (
                    <div key={`${insight.slow_fuel}-${cmp.benchmark_fuel}`} className="small text-muted border-top pt-2 mt-2">
                      <strong>{cmp.benchmark_fuel}:</strong>{" "}
                      {formatNumber(cmp.litres_per_cycle, 0)} L/cycle · {formatNumber(cmp.turnover_cycles_excess, 1)} turns
                      in {formatNumber(insight.excess_days, 0)} extra days · ₹{formatNumber(cmp.benchmark_margin_per_litre, 2)}/L margin
                      {cmp.missed_profit_full_trap > 0 ? (
                        <span className="d-block">
                          Full {formatNumber(insight.days_cover, 0)}-day trap ≈ {formatCurrency(cmp.missed_profit_full_trap)} vs {cmp.benchmark_fuel}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        ) : null}

        {fuelGrades.length > 0 ? (
          <>
            <h6 className="mb-2">Fuel grades — detail</h6>
            <div className="table-responsive mb-4">
              <table className="table table-sm owner-analytics-table mb-0">
                <thead>
                  <tr>
                    <th>Fuel</th>
                    <th>Status</th>
                    <th className="text-end">Sold (L)</th>
                    <th className="text-end">Stock (L)</th>
                    <th className="text-end">Tank fill</th>
                    <th className="text-end">Days cover</th>
                    <th className="text-end">Cash locked</th>
                    <th className="text-end">Margin / L</th>
                    <th className="text-end">FD cost / mo</th>
                    <th className="text-end">Profit vs interest</th>
                    <th>What to do</th>
                  </tr>
                </thead>
                <tbody>
                  {fuelGrades.map((row) => (
                    <tr
                      key={row.fuel}
                      className={`owner-wc-age-row ${getFuelStatusRowClass(row.status)}`}
                    >
                      <td><strong>{row.fuel}</strong></td>
                      <td>
                        <span className={`owner-wc-status ${statusClass(row.status)}`}>
                          {row.status_label}
                        </span>
                      </td>
                      <td className="text-end">{formatNumber(row.sold_litres_period, 0)}</td>
                      <td className="text-end">{formatNumber(row.litres, 0)}</td>
                      <td className="text-end">{formatNumber(row.fill_pct, 0)}%</td>
                      <td className="text-end">
                        {row.sold_litres_period <= 0
                          ? "No sales"
                          : row.days_cover != null
                            ? formatNumber(row.days_cover, 0)
                            : "—"}
                      </td>
                      <td className="text-end">{formatCurrency(row.locked_value)}</td>
                      <td className="text-end">
                        {row.margin_per_litre > 0 ? (
                          <span>₹{formatNumber(row.margin_per_litre, 2)}</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-end">{formatCurrency(row.monthly_interest_cost)}</td>
                      <td className="text-end">
                        {row.margin_pct > 0 ? (
                          <span className={row.margin_covers_interest ? "text-success" : "text-danger"}>
                            {row.margin_covers_interest ? "Profit wins" : "Interest wins"}
                            <span className="d-block small text-muted">
                              {formatNumber(row.margin_pct, 1)}% margin
                              {row.margin_source === "blended_fuel" ? " (avg)" : ""}
                            </span>
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="small text-muted">{row.recommendation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {slowNf.length > 0 ? (
          <>
            <h6 className="mb-2">Slow-moving shop / lube items</h6>
            <p className="small text-muted mb-2">
              Products idle 30+ days, never sold, or with more stock than recent sales justify — cash sitting on
              the shelf.
            </p>
            <div className="owner-wc-age-legend mb-2">
              <span className="owner-wc-age-legend__item">
                <span className="owner-wc-age-legend__swatch owner-wc-age-legend__swatch--fresh" />
                ≤30 days — fresh
              </span>
              <span className="owner-wc-age-legend__item">
                <span className="owner-wc-age-legend__swatch owner-wc-age-legend__swatch--aging" />
                31–60 days — aging
              </span>
              <span className="owner-wc-age-legend__item">
                <span className="owner-wc-age-legend__swatch owner-wc-age-legend__swatch--critical" />
                60+ days / never sold — stale
              </span>
            </div>
            <div className="table-responsive mb-4">
              <table className="table table-sm owner-analytics-table mb-0">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="text-end">Qty on hand</th>
                    <th className="text-end">Cash locked</th>
                    <th className="text-end">Last sale</th>
                    <th className="text-end">Days idle</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {slowNf.map((row) => {
                    const ageVisual = getStockIdleAgeVisual(row.days_since_sale);

                    return (
                      <tr
                        key={row.product}
                        className={`owner-wc-age-row ${ageVisual.rowClass}`}
                        style={{ backgroundColor: ageVisual.rowBg }}
                      >
                        <td><strong>{row.product}</strong></td>
                        <td className="text-end">{formatNumber(row.quantity, 0)}</td>
                        <td className="text-end">{formatCurrency(row.locked_value)}</td>
                        <td className="text-end">{row.last_sale_date || "Never"}</td>
                        <td className="text-end">
                          <StockAgeIndicator daysSinceSale={row.days_since_sale} />
                        </td>
                        <td className="small text-muted">{row.recommendation}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {lowMargin.length > 0 ? (
          <>
            <h6 className="mb-2">High volume but thin margin</h6>
            <p className="small text-muted mb-2">
              These sell a lot but earn little — push higher-margin items instead.
            </p>
            <div className="table-responsive">
              <table className="table table-sm owner-analytics-table mb-0">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="text-end">Sales</th>
                    <th className="text-end">Margin</th>
                    <th className="text-end">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {lowMargin.map((row) => {
                    const marginVisual = getMarginHeatVisual(row.margin_pct);

                    return (
                      <tr
                        key={`${row.product}-${row.category}`}
                        className={`owner-wc-age-row ${marginVisual.rowClass}`}
                        style={{ backgroundColor: marginVisual.rowBg }}
                      >
                        <td>{row.product}</td>
                        <td className="text-end">{formatCurrency(row.sales)}</td>
                        <td className="text-end">{formatCurrency(row.margin)}</td>
                        <td className="text-end">
                          <span className={`owner-wc-status ${marginVisual.badgeClass}`}>
                            {formatNumber(row.margin_pct, 1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
